/**
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ WHAT TO CALL A CONTRACT — ITS IDENTIFIER, ALWAYS (R-57).                    │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **13 Oct 2026, the product owner: the public identifier replaces the human code on every
 * screen and in every address.** `cont_12a3cd5a` is what a contract is called and where it lives;
 * `HSO-C01` stays on the wire (an agent's sentence, an old bookmark) and is still ACCEPTED as an
 * address by `contractMatches` below, but nothing the app draws or links to uses it.
 *
 * What stood here before, for the record — C56:
 * `HSO-C01` is built from a PROJECT's key prefix, so a contract under a client has none. Every
 * screen that printed `contract.code` would print an empty cell — a row that looks like data
 * somebody failed to enter rather than a contract of a different kind.
 *
 * ⚠️ **The identifier, not a dash.** `cont_12a3cd5a` is what `0152` exists for: a name that
 * can be quoted in a URL, a ticket or an integration, and every contract has one.
 */
export function contractLabel(contract: {
  readonly code: string | null;
  readonly identifier: string;
}): string {
  return contract.identifier;
}

/** The same, for a line item that carries its contract's two names. */
export function lineItemContractLabel(item: {
  readonly contractCode: string | null;
  readonly contractIdentifier: string;
}): string {
  return item.contractIdentifier;
}

/**
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ C56 · THE ADDRESS OF A CONTRACT, WHICH IS NOW EITHER OF ITS TWO NAMES.  │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * `/contracts/HSO-C01` is P2-9's shape and it assumes a code. A client-scoped contract has none,
 * so its address is its identifier — `/contracts/cont_12a3cd5a` — which is exactly the job
 * `0152` describes for it: *"what a URL, a ticket and an integration can quote"*.
 *
 * ⚠️ **One route, two accepted names, matched HERE rather than in three places.** The contracts
 * route resolved `candidate.code === params.code` in three spots; each would have had to learn
 * the fallback separately, and the one that was missed would 404 on a real contract.
 */
/* ⚠️ R-57 · links are identifiers now; a CODE is still accepted, so an agent's `HSO-C01`, a mail
   and a bookmark from before the ruling still open the right page. */
export function contractMatches(
  contract: { readonly code: string | null; readonly identifier: string },
  address: string | null | undefined,
): boolean {
  if (address === null || address === undefined) return false;
  return contract.code === address || contract.identifier === address;
}
